
import { defineComponent, ref, reactive, computed, watch, onMounted } from 'vue';
import { message, Modal } from 'ant-design-vue';
import { OioSpin, OioButton, OioTag, OioInput, OioForm, OioFormItem, OioPagination } from '@kunlun/vue-ui-antd';
import {
  BillingBill,
  BillingBillLine,
  DeliInvoiceInfo,
  BillingLineStatus,
  BillingType,
  BillCategory,
  PageQueryParams
} from '../types';
import {
  queryBillingBillDetail,
  querySellerBillingBillDetail,
  queryMerchantBillingBillDetail,
  queryIndependentBillingBillDetail,
  querySellerIndependentBillingBillDetail,
  queryBillingBillLines,
  queryEffectiveInvoiceInfo,
  queryCustomerInvoiceInfo,
  confirmBillingBill,
  partInvoice,
  cancelPackLines,
  updateBillingBill,
  resetInvoiceInfo,
  exportBillingLines,
  downloadInvoiceZip
} from '../api/billingBill';
import { useBillingStatus } from '../hooks/useBillingStatus';
import { useBillingAmount } from '../hooks/useBillingAmount';
import { usePortalType } from '../hooks/usePortalType';
import { useMatched } from '@kunlun/dependencies';
import {
  billingLineStatusTextMap,
  billingTypeTextMap,
  CANCELABLE_LINE_STATUSES,
  PACKABLE_LINE_STATUSES,
  SPECIFIABLE_LINE_STATUSES
} from '../constants';
import SpecifyInvoiceDialog from './SpecifyInvoiceDialog.vue';
import OperationLogDialog from './OperationLogDialog.vue';

export default defineComponent({
  name: 'BillingBillDetailPage',
  components: { SpecifyInvoiceDialog, OperationLogDialog, OioSpin, OioButton, OioTag, OioInput, OioForm, OioFormItem, OioPagination },
  props: {
    billId: { type: Number, default: 0 },
    mode: { type: String, default: '' },
    /**
     * 独立开票包显式标记：独立开票列表页内嵌本组件时传 true。
     * 不传时按 URL 中的代理模型名自动嗅探，嗅探结果再由详情返回的 billCategory 兜底修正。
     */
    independent: { type: Boolean, default: undefined }
  },
  setup(props) {
    const {
      getBillStatusText,
      getBillStatusColor,
      getLineStatusText,
      getLineStatusColor,
      canConfirm,
      canDownloadInvoice
    } = useBillingStatus();
    const { formatAmount, getAmountClass } = useBillingAmount();
    const { isDistributor, isSeller } = usePortalType();

    /**
     * 独立开票包判定。
     * 常规与独立开票的 queryPage 均已按 billCategory 收口，取错入口会直接查不到记录，
     * 故必须在 fetchData 之前先定位入口：父组件显式传入优先，其次按 URL 代理模型名嗅探，
     * 详情返回后再用 billCategory 兜底修正展示差异。
     */
    const detectIndependentFromUrl = (): boolean =>
      `${window.location.hash || ''}${window.location.href || ''}`.includes('IndependentBillingBill');
    const isIndependentBill = ref(
      props.independent === undefined ? detectIndependentFromUrl() : props.independent
    );

    const loading = ref(false);
    const lineLoading = ref(false);
    const showSpecifyDialog = ref(false);
    const logVisible = ref(false);
    // 折叠状态：基础信息默认展开，其余收起让表格尽可能多显示行
    const basicInfoExpanded = ref(true);
    const invoiceInfoExpanded = ref(false);
    const billData = ref<Partial<BillingBill>>({});
    const invoiceInfo = ref<Partial<DeliInvoiceInfo>>({});
    const customerInvoiceInfo = ref<Partial<DeliInvoiceInfo>>({});
    const invoiceInfoOptions = ref<DeliInvoiceInfo[]>([]);
    // 开票信息下拉搜索关键字：按编码/发票抬头本地模糊过滤（数据量小，无需后端检索）
    const invoiceInfoSearchKey = ref('');
    const filteredInvoiceInfoOptions = computed(() => {
      const keyword = invoiceInfoSearchKey.value.trim().toLowerCase();
      if (!keyword) return invoiceInfoOptions.value;
      return invoiceInfoOptions.value.filter(
        (info) =>
          (info.code || '').toLowerCase().includes(keyword) ||
          (info.invoiceTitle || '').toLowerCase().includes(keyword)
      );
    });
    const handleInvoiceInfoSearch = (val: string) => {
      invoiceInfoSearchKey.value = val || '';
    };
    const lineData = ref<BillingBillLine[]>([]);
    const selectedLineKeys = ref<number[]>([]);

    const lineTableRef = ref();

    // 编辑字段
    // 开票信息选择：开票信息记录ID；'default' 表示客户信息（默认）
    const DEFAULT_INVOICE_INFO = 'default';
    const selectedInvoiceInfoId = ref<number | string | undefined>(undefined);
    const editBillingType = ref<string>('');
    const editRemark = ref('');
    const editNoticeInvoiceNo = ref('');
    // 默认开票信息场景下的寄送地址（默认取经销商档案寄送地址，可手工修改），保存在开票单上
    const editDeliveryAddress = ref('');

    // 是否使用客户信息（默认）开票信息
    const isDefaultInvoiceInfo = computed(() => selectedInvoiceInfoId.value === DEFAULT_INVOICE_INFO);

    // 抽屉页当前开票信息选择（部分开票/指定开票/整单开票共用口径）：
    // 手动选择开票信息记录时传 invoiceInfoId；客户信息（默认）时传 resetInvoiceInfo 由后端清空已保存选择
    const invoiceSelectionPayload = computed(() => {
      const isDefaultInfo = selectedInvoiceInfoId.value === DEFAULT_INVOICE_INFO;
      return {
        billingType: getBillingTypeEnumName(editBillingType.value),
        remark: editRemark.value,
        invoiceInfoId: isDefaultInfo ? undefined : (selectedInvoiceInfoId.value as number),
        resetInvoiceInfo: isDefaultInfo,
        deliveryAddress: isDefaultInfo ? editDeliveryAddress.value : '',
        // 仅红字场景（实际打包行含负数单据）传红字通知单号，其余场景强制置空
        noticeInvoiceNo: hasNegativePackLines.value ? editNoticeInvoiceNo.value : ''
      };
    });

    const lineSearch = reactive({
      distributionChannel: '',
      tmsOrderCode: [] as string[],
      order90Code: [] as string[],
      status: undefined as string | undefined,
      dataPackageNo: ''
    });

    const linePagination = reactive({
      current: 1,
      pageSize: 50,
      total: 0
    });

    const selectedLines = computed(() => {
      return lineData.value.filter((line) => selectedLineKeys.value.includes(line.id));
    });

    /** 可取消打包：状态命中且持有主包号（SAP 按主包号整批取消，无主包号的行后端拦截不允许取消）。
     *  月度开票单镜像已消费90单状态生成的统计行没有数据包号，也不纳入可取消范围 */
    const hasCancelableLines = computed(() => {
      return selectedLines.value.some(
        (line) => CANCELABLE_LINE_STATUSES.includes(line.status) && !!line.dataPackageNo && !!line.mainPackageNo
      );
    });

    /** 独立开票包可重新发起打包：勾选行中存在未打包/已取消/开票失败（可发起打包）状态的行。
     *  取消打包后行回到已取消；开票失败行无需先取消旧包即可直接重发 SAP 打包 */
    const hasRepackableLines = computed(() => {
      return selectedLines.value.some(
        (line) => PACKABLE_LINE_STATUSES.includes(line.status)
      );
    });

    const allSelectedLinesSpecifiable = computed(() => {
      if (selectedLines.value.length === 0) return false;
      return selectedLines.value.every((line) => SPECIFIABLE_LINE_STATUSES.includes(line.status));
    });

    /** 实际参与打包的行：有勾选时取勾选行中的可打包行，无勾选（整单开票）时取当前全部可打包行。
     *  与后端 validateNoticeInvoiceNoByAmount 的判定口径保持一致 */
    const packRelevantLines = computed(() => {
      const source = selectedLines.value.length > 0 ? selectedLines.value : lineData.value;
      return source.filter((line) => PACKABLE_LINE_STATUSES.includes(line.status));
    });

    /** 实际打包行中是否存在负数单据（红字场景）：决定红字通知单号是否可填/必填 */
    const hasNegativePackLines = computed(() =>
      packRelevantLines.value.some((line) => (Number(line.lineAmount) || 0) < 0)
    );

    // 非红字场景（无负数单据）不允许填写红字通知单号，切换时清空已填值
    watch(hasNegativePackLines, (val) => {
      if (!val) editNoticeInvoiceNo.value = '';
    });

    /** 开票信息可编辑：edit 模式（常规单部分开票），或独立开票包（view 模式下的
     *  重新发起打包/重新发起指定开票携带开票信息提交，红票场景需可填写红字通知单号） */
    const invoiceInfoEditable = computed(() => isEditMode.value || isIndependentBill.value);

    const getBillId = (): number => {
      if (props.billId) return props.billId;
      // 优先从 Oinone 路由 segment 参数读取 id（router.push 携带）
      try {
        const { matched } = useMatched();
        const segId = (matched?.segmentParams?.page as Record<string, string> | undefined)?.id;
        if (segId) return Number(segId) || 0;
      } catch (e) {
        // 忽略，降级到 hash query
      }
      // 兼容 hash query 中的 ?id=
      const urlParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
      return Number(urlParams.get('id')) || 0;
    };

    /** 读取页面模式：view=详情（只读）/ edit=部分开票（开票信息可编辑）。
     *  优先使用组件 props.mode（弹窗内嵌场景），其次路由参数，最后 hash query。 */
    const getMode = (): string => {
      if (props.mode) return props.mode;
      try {
        const { matched } = useMatched();
        const segMode = (matched?.segmentParams?.page as Record<string, string> | undefined)?.mode;
        if (segMode) return segMode;
      } catch (e) {
        // 忽略，降级到 hash query
      }
      const urlParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
      return urlParams.get('mode') || 'view';
    };

    const isEditMode = ref(getMode() === 'edit');

    /** 将前端枚举值（如 'ZF1'）转为后端 GraphQL 枚举名（如 'VAT_SPECIAL'） */
    const getBillingTypeEnumName = (val: string): string => {
      const entry = Object.entries(BillingType).find(([_, v]) => v === val);
      return entry ? entry[0] : val;
    };
    
    // 开票类型可选项：仅增值税专用发票/增值税普通发票（电子发票、红票选项已隐藏；
    // 红字场景不再由开票类型区分，改由勾选行是否含负数单据驱动红字通知单号）
    const availableBillingTypes = computed(() => {
      const result: Record<string, string> = {};
      result[BillingType.VAT_SPECIAL] = billingTypeTextMap[BillingType.VAT_SPECIAL];
      result[BillingType.VAT_NORMAL] = billingTypeTextMap[BillingType.VAT_NORMAL];
      return result;
    });

    const buildLineRsql = (): string => {
      const conditions: string[] = ['(1==1)'];
      if (lineSearch.distributionChannel) conditions.push(`distributionChannel=like='%${lineSearch.distributionChannel}%'`);
      if (lineSearch.tmsOrderCode.length > 0) {
        const codes = lineSearch.tmsOrderCode.map(c => `'${c.trim()}'`).filter(c => c !== "''");
        if (codes.length > 0) conditions.push(`tmsOrderCode=in=(${codes.join(',')})`);
      }
      if (lineSearch.order90Code.length > 0) {
        const codes = lineSearch.order90Code.map(c => `'${c.trim()}'`).filter(c => c !== "''");
        if (codes.length > 0) conditions.push(`order90Code=in=(${codes.join(',')})`);
      }
      if (lineSearch.status) conditions.push(`status=='${lineSearch.status}'`);
      if (lineSearch.dataPackageNo) conditions.push(`dataPackageNo=='${lineSearch.dataPackageNo}'`);
      return conditions.join(' and ');
    };


    /** 常规开票包详情：经销工作台走 merchant 代理，品牌端走 seller 代理，运营中心走基础模型 */
    const loadNormalBillDetail = async (billId: number): Promise<BillingBill | null> => {
      if (isDistributor.value) {
        return (await queryMerchantBillingBillDetail(billId)) as BillingBill | null;
      }
      return isSeller.value
        ? ((await querySellerBillingBillDetail(billId)) as BillingBill | null)
        : ((await queryBillingBillDetail(billId)) as BillingBill | null);
    };

    /** 独立开票包详情：必须走 INDEPENDENT 过滤的代理模型入口，基础模型与常规代理均查不到 */
    const loadIndependentBillDetail = async (billId: number): Promise<BillingBill | null> =>
      isSeller.value
        ? await querySellerIndependentBillingBillDetail(billId)
        : await queryIndependentBillingBillDetail(billId);

    /**
     * 按判定结果选择详情入口；主入口查不到时回退到另一入口再试一次，
     * 避免 URL 嗅探误判导致详情页空白（经销工作台不开放独立开票，不参与回退）。
     */
    const loadBillDetail = async (billId: number): Promise<BillingBill | null> => {
      if (isDistributor.value) return loadNormalBillDetail(billId);
      const bill = isIndependentBill.value
        ? await loadIndependentBillDetail(billId)
        : await loadNormalBillDetail(billId);
      if (bill && bill.id) return bill;
      return isIndependentBill.value
        ? await loadNormalBillDetail(billId)
        : await loadIndependentBillDetail(billId);
    };

    const fetchData = async () => {
      const billId = getBillId();
      if (!billId) return;
      loading.value = true;
      try {
        const bill = await loadBillDetail(billId);
        if (!bill) return;
        billData.value = bill;
        // 入口嗅探可能有误，展示差异一律以详情返回的 billCategory 为准
        isIndependentBill.value = bill.billCategory === BillCategory.INDEPENDENT;
        // 初始化编辑字段（开票类型未设置时默认取经销商档案的开票类型）
        editBillingType.value = bill.billingType || '';
        editRemark.value = bill.remark || '';
        editNoticeInvoiceNo.value = bill.noticeInvoiceNo || '';
        editDeliveryAddress.value = bill.deliveryAddress || '';
        // 默认取客户信息（经销商档案）中的开票信息，若无则为空
        if (bill.distributorCode) {
          const customerInfo = await queryCustomerInvoiceInfo(bill.distributorCode);
          customerInvoiceInfo.value = customerInfo || {};
          invoiceInfo.value = customerInfo || {};
          if (!editBillingType.value) {
            editBillingType.value = customerInfo?.defaultBillingType || BillingType.VAT_SPECIAL;
          }
          // 开票单未保存过寄送地址时，默认展示经销商档案的寄送地址
          if (!editDeliveryAddress.value) {
            editDeliveryAddress.value = customerInfo?.deliveryAddress || '';
          }
        }
        if (!editBillingType.value) {
          editBillingType.value = BillingType.VAT_SPECIAL;
        }
        // 加载开票信息列表（作为备选选项）
        if (bill.distributorCode && bill.salesOrgCode) {
          const infos = await queryEffectiveInvoiceInfo(bill.distributorCode, bill.salesOrgCode);
          invoiceInfoOptions.value = infos as DeliInvoiceInfo[];
          // 选项重载后同步复位搜索过滤条件
          invoiceInfoSearchKey.value = '';
          // 开票单已保存过开票信息时回显选中，否则展示客户信息（默认）
          const saved = infos.find((i: any) => i.id === bill.invoiceInfoId);
          if (saved) {
            invoiceInfo.value = saved;
            selectedInvoiceInfoId.value = saved.id;
          } else {
            selectedInvoiceInfoId.value = DEFAULT_INVOICE_INFO;
          }
        }
        fetchLines();
      } catch (e: any) {
        // 失败提示由框架右上角 notification 统一处理，避免重复弹窗
      } finally {
        loading.value = false;
      }
    };

    const fetchLines = async () => {
      const billId = getBillId();
      if (!billId) return;
      lineLoading.value = true;
      try {
        const params: PageQueryParams = {
          currentPage: linePagination.current,
          size: linePagination.pageSize,
          rsql: buildLineRsql()
        };
        const result = await queryBillingBillLines(billId, params);
        lineData.value = result.content || [];
        linePagination.total = result.totalElements || 0;
        // 数据刷新后清空选中状态，避免vxe-table非受控checkbox与selectedLineKeys不一致
        selectedLineKeys.value = [];
        lineTableRef.value?.clearCheckboxRow?.();
      } catch (e: any) {
        // 失败提示由框架右上角 notification 统一处理，避免重复弹窗
      } finally {
        lineLoading.value = false;
      }
    };

    const handleInvoiceInfoChange = (infoId: number | string | undefined) => {
      // 选中/清除后同步清空搜索词（输入框由组件自动清空，列表过滤条件需手动复位）
      invoiceInfoSearchKey.value = '';
      // 点清除图标清空时，归一为默认选项
      if (infoId === undefined || infoId === null) {
        selectedInvoiceInfoId.value = DEFAULT_INVOICE_INFO;
        invoiceInfo.value = customerInvoiceInfo.value;
        return;
      }
      const selected = invoiceInfoOptions.value.find(i => i.id === infoId);
      if (selected) {
        invoiceInfo.value = selected;
      } else {
        // 选择“客户信息（默认）”：回退展示客户信息中的开票信息
        invoiceInfo.value = customerInvoiceInfo.value;
      }
    };

    const handleLinePaginationChange = (page: number, pageSize: number) => {
      linePagination.current = page;
      linePagination.pageSize = pageSize;
      fetchLines();
    };

    const onLineCheckboxChange = ({ records }: any) => {
      selectedLineKeys.value = (records || []).map((r: BillingBillLine) => r.id);
    };

    const handleConfirmBill = async () => {
      // 存在负数单据（红字场景）必须填写红字通知单号
      if (hasNegativePackLines.value && !editNoticeInvoiceNo.value.trim()) {
        message.error('存在负数单据，必须填写红字通知单号');
        return;
      }
      Modal.confirm({
        title: '整单开票',
        content: '确定将开票单内剩余未开票明细全部发起开票吗？',
        okText: '确定',
        cancelText: '取消',
        onOk: async () => {
          loading.value = true;
          try {
            // 先保存当前编辑的开票类型/红字通知单号/寄送地址/开票信息选择，确保后端按最新值校验
            const isDefaultInfo = selectedInvoiceInfoId.value === DEFAULT_INVOICE_INFO;
            await updateBillingBill(getBillId(), {
              billingType: getBillingTypeEnumName(editBillingType.value),
              remark: editRemark.value,
              ...(isDefaultInfo ? {} : { invoiceInfoId: selectedInvoiceInfoId.value }),
              // 寄送地址：默认开票信息时保存手工填写值；选择开票信息记录时清空，避免残留覆盖
              deliveryAddress: isDefaultInfo ? editDeliveryAddress.value : '',
              // 仅红字场景（实际打包行含负数单据）传红字通知单号，其余场景强制置空
              noticeInvoiceNo: hasNegativePackLines.value ? editNoticeInvoiceNo.value : ''
            } as any);
            // 切回默认：先清空已保存的开票信息ID，保持页面选择与单据状态一致
            if (selectedInvoiceInfoId.value === DEFAULT_INVOICE_INFO) {
              await resetInvoiceInfo(getBillId());
            }
            await confirmBillingBill(billData.value.id);
            message.success('整单开票已发起');
            fetchData();
            fetchLines();
          } catch (e: any) {
            message.error(e?.message || '整单开票失败');
          } finally {
            loading.value = false;
          }
        }
      });
    };

    const handlePartInvoice = () => {
      if (selectedLines.value.length === 0) {
        message.warning('请先勾选需要开票的明细行');
        return;
      }
      // 存在负数单据（红字场景）必须填写红字通知单号
      if (hasNegativePackLines.value && !editNoticeInvoiceNo.value.trim()) {
        message.error('存在负数单据，必须填写红字通知单号');
        return;
      }
      // 校验选中行状态：仅未打包/已取消/开票失败状态可开票
      const packableLines = selectedLines.value.filter(l =>
        SPECIFIABLE_LINE_STATUSES.includes(l.status)
      );
      if (packableLines.length === 0) {
        message.warning('仅"未打包"、"已取消"或"开票失败"状态的单据可发起开票');
        return;
      }
      const unPackableCount = selectedLines.value.length - packableLines.length;
      const content = unPackableCount > 0
        ? `已选 ${selectedLines.value.length} 行，其中 ${packableLines.length} 行可开票，${unPackableCount} 行状态不可开票（将跳过）。确定使用当前开票信息发起部分开票吗？`
        : `确定使用当前开票信息对已选 ${packableLines.length} 行明细发起部分开票吗？`;
      Modal.confirm({
        zIndex: 2000,
        title: '部分开票',
        content,
        onOk: async () => {
          try {
            // 调用后端统一接口：保存开票信息 + 部分打包
            await partInvoice(getBillId(), {
              ...invoiceSelectionPayload.value,
              partPackLineIds: packableLines.map(l => l.id)
            });
            message.success('部分开票已提交');
            fetchData();
          } catch (e: any) {
            // 失败提示由框架右上角 notification 统一处理，避免重复弹窗
          }
        }
      });
    };

    const handleSpecifyInvoice = () => {
      if (!allSelectedLinesSpecifiable.value) {
        message.warning('仅可选择"未打包"、"已取消"或"开票失败"状态的单据进行指定开票');
        return;
      }
      showSpecifyDialog.value = true;
    };

    /** 独立开票包重新发起打包：取消打包后包内行回到"已取消"，可重新发起 SAP 打包。
     *  若当期经销商开票单已生成（90单已被常规单镜像纳入），后端会拦截并提示前往经销商开票页面操作 */
    const handleRepack = () => {
      if (selectedLines.value.length === 0) {
        message.warning('请先勾选需要重新发起打包的明细行');
        return;
      }
      // 存在负数单据（红字场景）必须填写红字通知单号
      if (hasNegativePackLines.value && !editNoticeInvoiceNo.value.trim()) {
        message.error('存在负数单据，必须填写红字通知单号');
        return;
      }
      const packableLines = selectedLines.value.filter(l =>
        PACKABLE_LINE_STATUSES.includes(l.status)
      );
      if (packableLines.length === 0) {
        message.warning('仅"未打包"、"已取消"或"开票失败"状态的单据可发起打包');
        return;
      }
      Modal.confirm({
        zIndex: 2000,
        title: '重新发起打包',
        content: `确定使用当前开票信息对已选 ${packableLines.length} 行单据重新发起打包吗？`,
        onOk: async () => {
          try {
            await partInvoice(getBillId(), {
              ...invoiceSelectionPayload.value,
              partPackLineIds: packableLines.map(l => l.id)
            });
            message.success('重新发起打包已提交');
            fetchData();
          } catch (e: any) {
            // 失败提示由框架右上角 notification 统一处理（含后端"经销商开票单已生成"拦截提示），避免重复弹窗
          }
        }
      });
    };

    const handleCancelPack = () => {
      const cancelableLines = selectedLines.value.filter(
        (l) => CANCELABLE_LINE_STATUSES.includes(l.status) && !!l.dataPackageNo && !!l.mainPackageNo
      );
      if (cancelableLines.length === 0) {
        message.warning('请选择"已打包"或"开票失败"且已有主包号的单据进行取消打包');
        return;
      }
      Modal.confirm({
        zIndex: 2000,
        title: '取消打包',
        content: `取消按主包号整批执行：已勾选 ${cancelableLines.length} 条单据所在主包的全部单据（含未勾选的）都将一并置为已取消，确定继续吗？`,
        onOk: async () => {
          try {
            const lineIds = cancelableLines.map(l => l.id);
            await cancelPackLines(lineIds);
            message.success('取消打包成功');
            fetchData();
          } catch (e: any) {
            message.error(e?.message || '取消打包失败');
          }
        }
      });
    };

    /** 下载发票：后端打包 zip 后返回下载地址 */
    const handleDownloadInvoice = async () => {
      try {
        const url = await downloadInvoiceZip(getBillId());
        if (url) {
          window.open(url, '_blank');
          message.success('发票打包下载已开始');
        } else {
          message.warning('未找到可下载的发票文件');
        }
      } catch (e: any) {
        message.error(e?.message || '下载发票失败');
      }
    };

    /** 导出明细：导出该开票单关联单据 Excel 并下载 */
    const handleExport = async () => {
      try {
        const url = await exportBillingLines(getBillId(), []);
        if (url) {
          window.open(url, '_blank');
        } else {
          message.warning('导出地址获取失败');
        }
      } catch (e: any) {
        // 失败提示由框架右上角 notification 统一处理，避免重复弹窗
      }
    };

    /** 打开操作日志弹窗 */
    const handleLog = () => {
      if (!getBillId()) {
        message.warning('当前开票单信息未加载完成');
        return;
      }
      logVisible.value = true;
    };

    const formatDate = (dateStr: string | undefined) => {
      if (!dateStr) return '-';
      return dateStr.substring(0, 10);
    };

    onMounted(() => {
      fetchData();
    });

    return {
      loading,
      lineLoading,
      billData,
      invoiceInfo,
      invoiceInfoOptions,
      filteredInvoiceInfoOptions,
      lineData,
      selectedLineKeys,
      selectedLines,
      invoiceSelectionPayload,
      lineSearch,
      linePagination,
      lineTableRef,
      hasCancelableLines,
      hasRepackableLines,
      allSelectedLinesSpecifiable,
      hasNegativePackLines,
      showSpecifyDialog,
      logVisible,
      basicInfoExpanded,
      invoiceInfoExpanded,
      selectedInvoiceInfoId,
      isDefaultInvoiceInfo,
      editBillingType,
      handleInvoiceInfoSearch,
      editRemark,
      editNoticeInvoiceNo,
      editDeliveryAddress,
      BillingType,
      isEditMode,
      invoiceInfoEditable,
      isDistributor,
      isIndependentBill,
      billingLineStatusTextMap,
      billingTypeTextMap,
      availableBillingTypes,
      getBillStatusText,
      getBillStatusColor,
      getLineStatusText,
      getLineStatusColor,
      canConfirm,
      canDownloadInvoice,
      formatAmount,
      getAmountClass,
      formatDate,
      fetchData,
      fetchLines,
      handleInvoiceInfoChange,
      handleLinePaginationChange,
      onLineCheckboxChange,
      handleConfirmBill,
      handlePartInvoice,
      handleSpecifyInvoice,
      handleRepack,
      handleCancelPack,
      handleDownloadInvoice,
      handleExport,
      handleLog
    };
  }
});
